// SPDX-License-Identifier: BUSL-1.1
/**
 * Fleet YAML schema v1 — declarative agent fleet configuration.
 *
 * Like docker-compose.yml but for AI agent fleets. Defines agents,
 * their budgets, policy gates, and operational constraints.
 *
 * Note: avoids z.default() and z.infer because the ambient zod-shim.d.ts
 * (used during partial installs) does not declare them. Types are defined
 * explicitly instead.
 *
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Sub-schemas
// ---------------------------------------------------------------------------
/** Per-tool policy gate override. */
const toolPolicySchema = z.object({
    name: z.string(),
    gate: z.enum(['safe', 'guarded', 'manual']),
});
/** Budget configuration for cost governance. */
const budgetSchema = z.object({
    limit: z.number().positive(),
    scope: z.enum(['session', 'daily', 'weekly', 'monthly']).optional(),
    action: z.enum(['warn', 'pause', 'terminate']).optional(),
});
/** Policy configuration (default gate + per-tool overrides). */
const policySchema = z.object({
    default: z.enum(['safe', 'guarded', 'manual']),
    tools: z.array(toolPolicySchema).optional(),
});
/** Single agent definition within a fleet. */
const agentSchema = z.object({
    id: z.string().min(1),
    name: z.string().optional(),
    model: z.string().optional(),
    environment: z.string(),
    instructions: z.string().optional(),
    budget: budgetSchema.optional(),
    policy: policySchema.optional(),
    tags: z.array(z.string()).optional(),
    autoStart: z.boolean().optional(),
});
/** Fleet-level defaults applied to all agents (agent-level overrides win). */
const fleetDefaultsSchema = z.object({
    model: z.string().optional(),
    budget: budgetSchema.optional(),
    policy: z.object({
        default: z.enum(['safe', 'guarded', 'manual']).optional(),
    }).optional(),
});
/** Top-level fleet definition (version + fleet block). */
export const fleetSchema = z.object({
    version: z.literal('v1'),
    fleet: z.object({
        name: z.string().min(1),
        description: z.string().optional(),
        defaults: fleetDefaultsSchema.optional(),
        agents: z.array(agentSchema).min(1),
    }),
});
//# sourceMappingURL=schema.js.map